import { ChevronLeft, ChevronRight, PhoneOutlined, WhatsApp } from '@mui/icons-material'
import { Box, Container, Link, Paper, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import type { ContactSupportData } from '@/src/constants/contactSupport'
import { buildWhatsAppSendUrl, getWhatsAppSendPhoneDigits } from '@/src/utils/contact/whatsappSendUrl'

/** Phone / link number emphasis (matches existing primary-style blue on contact card). */
const CONTACT_PHONE_DISPLAY_COLOR = '#2A5298'
const WHATSAPP_ICON_GREEN = '#25D366'

const contactCardRowSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    py: 1.5,
    px: 0.5,
    mx: -0.5,
    borderBottom: 1,
    borderColor: 'divider',
    borderRadius: 1
} as const

export interface ContactSupportCopy {
    customerService: string
    forWhatsApp: string
    callsFrom: string
    internationalWhatsapp: string
    officeAddressTitle: string
    contactNumber: string
    registeredNumber: string
    vat: string
}

export interface ContactFormViewProps {
    supportData: ContactSupportData
    isRtl: boolean
    copy: ContactSupportCopy
}

export const ContactFormView: React.FC<ContactFormViewProps> = ({ supportData, isRtl, copy }) => {
    const officeContactDisplay = supportData.officeContactNumber ?? supportData.contactNumber

    const whatsappPhoneDigits = useMemo(() => getWhatsAppSendPhoneDigits(supportData), [supportData])
    const whatsappHref = whatsappPhoneDigits ? buildWhatsAppSendUrl(whatsappPhoneDigits) : null
    const whatsappDisplayNumber = useMemo(
        () =>
            supportData.internationalLines?.find(l => l.kind === 'whatsapp')?.value ??
            supportData.contactNumber,
        [supportData]
    )

    const internationalLinesNonWhatsapp = useMemo(
        () => supportData.internationalLines?.filter(l => l.kind !== 'whatsapp') ?? [],
        [supportData]
    )

    return (
        <Box
            sx={{
                py: 3,
                flex: 1,
                bgcolor: 'grey.50',
                direction: isRtl ? 'rtl' : 'ltr'
            }}
        >
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: 'flex',
                        gap: { xs: 3, lg: 4 },
                        flexDirection: { xs: 'column', lg: isRtl ? 'row-reverse' : 'row' },
                        alignItems: 'stretch',
                        width: '100%'
                    }}
                >
                    <Box sx={{ flex: { xs: '1 1 auto', lg: '1 1 0' }, minWidth: 0, width: '100%' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                overflow: 'hidden',
                                borderRadius: 2,
                                height: '100%',
                                border: 1,
                                borderColor: 'borders.default'
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    px: 2.5,
                                    py: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    flexDirection: 'row'
                                }}
                            >
                                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                    {supportData.workingHours}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'inherit', fontWeight: 500, lineHeight: 1.2 }}>
                                    {copy.customerService}
                                </Typography>
                            </Box>
                            <Box sx={{ bgcolor: 'grey.100', p: 2.5, textAlign: 'start' }}>
                                <Stack spacing={0} useFlexGap sx={{ gap: 2.5, width: '100%', alignItems: 'stretch' }}>
                                    {whatsappHref && (
                                        <Link
                                            href={whatsappHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="none"
                                            sx={{
                                                ...contactCardRowSx,
                                                color: 'inherit',
                                                textDecoration: 'none',
                                                transition: theme => theme.transitions.create('background-color'),
                                                '&:hover': {
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <WhatsApp
                                                sx={{
                                                    fontSize: 28,
                                                    color: WHATSAPP_ICON_GREEN,
                                                    flexShrink: 0
                                                }}
                                            />
                                            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}
                                                >
                                                    {copy.forWhatsApp}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: CONTACT_PHONE_DISPLAY_COLOR,
                                                        lineHeight: 1.35
                                                    }}
                                                >
                                                    {whatsappDisplayNumber}
                                                </Typography>
                                            </Stack>
                                            {isRtl ? (
                                                <ChevronLeft
                                                    sx={{ fontSize: 22, color: 'text.secondary', flexShrink: 0 }}
                                                />
                                            ) : (
                                                <ChevronRight
                                                    sx={{ fontSize: 22, color: 'text.secondary', flexShrink: 0 }}
                                                />
                                            )}
                                        </Link>
                                    )}
                                    <Box sx={{ ...contactCardRowSx, width: '100%' }}>
                                        <PhoneOutlined
                                            sx={{ fontSize: 28, color: 'text.secondary', flexShrink: 0 }}
                                        />
                                        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}
                                            >
                                                {copy.callsFrom}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: CONTACT_PHONE_DISPLAY_COLOR,
                                                    lineHeight: 1.35
                                                }}
                                            >
                                                {supportData.phone}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                    {internationalLinesNonWhatsapp.map((line, i) => (
                                        <Box
                                            key={`${line.kind}-${line.value}-${i}`}
                                            sx={{ ...contactCardRowSx, width: '100%' }}
                                        >
                                            <PhoneOutlined
                                                sx={{ fontSize: 28, color: 'text.secondary', flexShrink: 0 }}
                                            />
                                            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontSize: '0.8125rem',
                                                        lineHeight: 1.35,
                                                        visibility: i === 0 ? 'visible' : 'hidden'
                                                    }}
                                                    aria-hidden={i !== 0}
                                                >
                                                    {copy.internationalWhatsapp}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: CONTACT_PHONE_DISPLAY_COLOR,
                                                        lineHeight: 1.35
                                                    }}
                                                >
                                                    {line.value}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 auto', lg: '1 1 0' }, minWidth: 0, width: '100%' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                p: 2.5,
                                bgcolor: 'grey.100',
                                border: 1,
                                borderColor: 'borders.default',
                                textAlign: 'start',
                                height: '100%'
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, mb: 1, color: 'primary.main', textAlign: 'start' }}
                            >
                                {copy.officeAddressTitle}
                            </Typography>
                            {!!supportData.companyName && (
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 700, mb: 1, color: 'text.primary', textAlign: 'start' }}
                                >
                                    {supportData.companyName}
                                </Typography>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 2,
                                    color: 'text.secondary',
                                    whiteSpace: 'pre-line',
                                    textAlign: 'start'
                                }}
                            >
                                {supportData.officeAddress}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}>
                                <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {copy.contactNumber}
                                </Typography>{' '}
                                {officeContactDisplay}
                            </Typography>
                            {!!supportData.registeredNumber && (
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}
                                >
                                    <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {copy.registeredNumber}
                                    </Typography>{' '}
                                    {supportData.registeredNumber}
                                </Typography>
                            )}
                            {!!supportData.vat && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'start' }}>
                                    <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {copy.vat}
                                    </Typography>{' '}
                                    {supportData.vat}
                                </Typography>
                            )}
                        </Paper>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}
